import { useState, type FormEvent } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  formatDate,
  Input,
  PersonDisplay,
} from '@dataroom/shared';
import type { RoomAdmin } from '@dataroom/shared';
import { useAddRoomAdmin, useRemoveRoomAdmin, useRoomAdmins } from '@/api/hooks';
import { PlanLimitDialog } from '@/components/PlanLimitDialog';
import { plainError } from '@/lib/errors';
import { planLimitOf } from '@/lib/plan';

/**
 * "Room admins" (migration 0069, ADR-0049): the room's main admin and the
 * colleagues from the same firm who run the room with them. A co-admin has the
 * same powers in this room; everything they do is recorded under their own name.
 * Only the main admin or the firm's owner adds and removes co-admins (the API
 * says so in `canManage`); a co-admin sees the list read-only.
 */
export function RoomAdminsCard({ roomId }: { roomId: string }) {
  const admins = useRoomAdmins(roomId);
  const add = useAddRoomAdmin(roomId);
  const remove = useRemoveRoomAdmin(roomId);
  const [email, setEmail] = useState('');
  const [removing, setRemoving] = useState<RoomAdmin | null>(null);
  const canManage = admins.data?.canManage === true;
  // No free admin seat (409 seat_limit_reached) → the plan-limit dialog, like Team.
  const seatLimit = planLimitOf(add.error);

  function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    add.mutate({ email: trimmed }, { onSuccess: () => setEmail('') });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Room admins</CardTitle>
        <p className="text-sm text-muted-foreground">
          Colleagues from your firm who run this room with you. They can do everything you can here
          — invite buyers, release checklist phases, accept LOIs. Everything they do is recorded
          under their own name in the room&apos;s activity.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {admins.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : admins.error ? (
          <p className="text-sm text-destructive">{plainError(admins.error)}</p>
        ) : (
          <ul className="divide-y" aria-label="Room admins">
            {admins.data.admins.map((admin) => (
              <li key={admin.actorId} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <PersonDisplay email={admin.email} name={admin.displayName} />
                    {admin.isMain ? (
                      <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                        Main admin
                      </span>
                    ) : null}
                  </div>
                  {!admin.isMain && admin.addedAt ? (
                    <p className="text-xs text-muted-foreground">
                      Added {formatDate(admin.addedAt)}
                      {admin.addedByEmail ? ` by ${admin.addedByEmail}` : ''}
                    </p>
                  ) : null}
                </div>
                {canManage && !admin.isMain ? (
                  <button
                    type="button"
                    onClick={() => setRemoving(admin)}
                    disabled={remove.isPending}
                    className="text-xs font-medium text-destructive hover:underline disabled:opacity-50"
                  >
                    Remove
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {plainError(remove.error) ? (
          <p className="text-sm text-destructive">{plainError(remove.error)}</p>
        ) : null}

        {canManage ? (
          <form className="space-y-1.5" onSubmit={submit}>
            <label htmlFor={`room-admin-email-${roomId}`} className="text-sm font-medium">
              Add a colleague as an admin
            </label>
            <div className="flex gap-2">
              <Input
                id={`room-admin-email-${roomId}`}
                type="email"
                placeholder="colleague@yourfirm.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button type="submit" disabled={add.isPending}>
                {add.isPending ? 'Adding…' : 'Add admin'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Each room admin uses one of your plan&apos;s admin seats (one seat per person, however
              many rooms). If they don&apos;t have an account yet, we email them an invitation.
            </p>
            {plainError(add.error) && !seatLimit ? (
              <p className="text-sm text-destructive">{plainError(add.error)}</p>
            ) : null}
          </form>
        ) : admins.data ? (
          <p className="text-xs text-muted-foreground">
            Only the room&apos;s main admin or your firm&apos;s owner can add or remove room admins.
          </p>
        ) : null}
      </CardContent>
      {removing ? (
        <ConfirmDialog
          danger
          title={`Remove ${removing.email} as a room admin?`}
          confirmLabel="Remove admin"
          pending={remove.isPending}
          onCancel={() => setRemoving(null)}
          onConfirm={() => remove.mutate(removing.actorId, { onSettled: () => setRemoving(null) })}
        >
          <p>
            They lose access to this room&apos;s admin pages right away. What they did here stays in
            the room&apos;s activity.
          </p>
        </ConfirmDialog>
      ) : null}
      {seatLimit ? <PlanLimitDialog info={seatLimit} onClose={() => add.reset()} /> : null}
    </Card>
  );
}
